import React, { useState } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Button,
  Row,
  Col,
  Tag,
} from 'antd';
import { CharacterItem, CreateNodeRequest } from '../api/models/Lifetree';

const { TextArea } = Input;
const { Option } = Select;

interface AddNodeModalProps {
  visible: boolean;
  onCancel: () => void;
  onSubmit: (nodeData: CreateNodeRequest) => Promise<void>;
  characters: CharacterItem[];
  loading?: boolean;
}

const AddNodeModal: React.FC<AddNodeModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  characters,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [tags, setTags] = useState<string[]>([]);
  const [inputTag, setInputTag] = useState('');

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const nodeData: CreateNodeRequest = {
        title: values.title,
        description: values.description,
        imageUrl: values.imageUrl,
        characterStateIds: values.characterStateIds || [],
        level: values.level !== undefined ? values.level : null,
        requiredExp: values.requiredExp || 0,
        tags: tags,
      };
      
      await onSubmit(nodeData);
      handleCancel();
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setTags([]);
    setInputTag('');
    onCancel();
  };

  const handleAddTag = () => {
    if (inputTag && !tags.includes(inputTag)) {
      setTags([...tags, inputTag]);
      setInputTag('');
    }
  };

  const handleRemoveTag = (removedTag: string) => {
    setTags(tags.filter(tag => tag !== removedTag));
  };

  // Tạo danh sách character states từ tất cả characters
  const characterStateOptions = characters.flatMap(character =>
    character.states.map(state => ({
      value: state.id,
      label: `${character.name} - ${state.name} (Age ${state.age})`,
      characterName: character.name,
      stateName: state.name,
    }))
  );

  return (
    <Modal
      title="Add New Node"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={800}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          requiredExp: 0,
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="title"
              label="Title"
              rules={[
                { required: true, message: 'Please enter node title' },
                { max: 255, message: 'Title cannot exceed 255 characters' },
              ]}
            >
              <Input placeholder="Enter node title" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="level"
              label="Level"
              rules={[
                { type: 'number', min: 0, message: 'Level must be at least 0' },
              ]}
            >
              <InputNumber min={0} max={10} placeholder="Auto assign" style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name="requiredExp"
              label="Required EXP"
              rules={[
                { type: 'number', min: 0, message: 'Required EXP must be at least 0' },
              ]}
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { max: 1000, message: 'Description cannot exceed 1000 characters' },
          ]}
        >
          <TextArea
            rows={3}
            placeholder="Enter node description"
            showCount
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item
          name="imageUrl"
          label="Image URL"
          rules={[
            { max: 255, message: 'Image URL cannot exceed 255 characters' },
            { type: 'url', message: 'Please enter a valid URL' },
          ]}
        >
          <Input placeholder="Enter image URL (optional)" />
        </Form.Item>

        <Form.Item
          name="characterStateIds"
          label="Character States"
        >
          <Select
            mode="multiple"
            placeholder="Select character states"
            style={{ width: '100%' }}
            optionFilterProp="label"
            showSearch
          >
            {characterStateOptions.map(option => (
              <Option key={option.value} value={option.value} label={option.label}>
                <div>
                  <strong>{option.characterName}</strong> - {option.stateName}
                </div>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item label="Tags">
          <div style={{ marginBottom: 8 }}>
            {tags.map(tag => (
              <Tag
                key={tag}
                closable
                onClose={() => handleRemoveTag(tag)}
                style={{ marginBottom: 4 }}
              >
                {tag}
              </Tag>
            ))}
          </div>
          <Row gutter={8}>
            <Col flex="auto">
              <Input
                value={inputTag}
                onChange={(e) => setInputTag(e.target.value)}
                onPressEnter={handleAddTag}
                placeholder="Enter tag and press Enter"
                maxLength={50}
              />
            </Col>
            <Col>
              <Button onClick={handleAddTag} disabled={!inputTag}>
                Add Tag
              </Button>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddNodeModal; 